import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturb';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import MusicOffIcon from '@mui/icons-material/MusicOff';
import StarIcon from '@mui/icons-material/Star';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbsUpDownIcon from '@mui/icons-material/ThumbsUpDown';
import { Badge, Box, Chip, SvgIcon, Tooltip, Typography, alpha, useTheme } from '@mui/material';
import { Stack } from '@mui/system';
import {
  DataGrid,
  DataGridProps,
  GridColDef,
  GridRenderCellParams,
  GridRowClassNameParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import { GridInitialStateCommunity } from '@mui/x-data-grid/models/gridStateCommunity';
import { DataGridColumnWithSubtitle } from 'components/common/DataGridColumnMultiline';
import { ClientComponent } from 'components/guardedComponent/ClientComponent';
import { TrackCover } from 'components/track/TrackCover';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { deepmergeCustom } from 'deepmerge-ts';
import { Track } from 'models/Track';
import {
  TrackRequest,
  TrackRequestState,
  TrackRequestType,
  TrackRequesterPriorityValue,
} from 'models/TrackRequest';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDuration } from 'utils/formatDuration';
import { RequestMapButton } from './RequestMapButton';

const deepmergeInitialState = deepmergeCustom<GridInitialStateCommunity | undefined>({
  mergeArrays: false,
});

export enum MapOption {
  DIRECT,
  MAP,
}

export type RequestListProps = Omit<DataGridProps<TrackRequest>, 'rows' | 'columns'> & {
  requests: TrackRequest[];
  selectedRequests: TrackRequest[];
  onMapButtonClick: (row: any, option: MapOption) => void;
  onSelectRequest?: (requests: TrackRequest[]) => void;
  actions?: (params: GridRenderCellParams<TrackRequest>) => JSX.Element;
  initialState?: GridInitialStateCommunity;
  requestColumns?: (keyof TrackRequest | 'state' | 'count')[];
};

export const RequestList: React.FC<RequestListProps> = ({
  requests,
  selectedRequests,
  onMapButtonClick,
  onSelectRequest,
  actions,
  requestColumns,
  slots,
  ...rest
}) => {
  const { t } = useTranslation();
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set(),
  });
  const theme = useTheme();
  dayjs.extend(localizedFormat);
  dayjs.extend(duration);

  // const columnsVisibility = preferences.defaultRequestColumns?.length
  //   ? Object.fromEntries(preferences.defaultRequestColumns.map((col) => [col, false]))
  //   : {
  //       bpm: false,
  //       key: false,
  //       collection: false,
  //       genre: false,
  //       path: false,
  //     };

  const getRowClassName = (params: GridRowClassNameParams<TrackRequest>) => {
    const priorities = params.row.priorities ?? [];
    const messages = params.row.messages ?? [];
    const totalVotes = params.row.requesters?.length ?? 0;

    const hasLow = priorities.some((p) => p.value === TrackRequesterPriorityValue.Low);
    const hasHigh = priorities.some((p) => p.value === TrackRequesterPriorityValue.High);
    const hasNoVotes = totalVotes === 0 && priorities.length === 0 && messages.length === 0;

    if (hasNoVotes) return 'row-no-votes';
    if (hasLow) return 'row-priority-low';
    if (hasHigh) return 'row-priority-high';
    return '';
  };

  rest.initialState = deepmergeInitialState(
    {
      pagination: {
        paginationModel: {
          pageSize: -1,
        },
      },
      columns: {
        columnVisibilityModel: {
          bpm: false,
          key: false,
          collection: false,
          genre: false,
          path: false,
          vipMessages: false,
          count: false,
        },
      },
      sorting: {
        sortModel: [
          {
            field: 'date',
            sort: 'desc',
          },
        ],
      },
    },
    rest.initialState
  );

  const requestListColumns: GridColDef[] = useMemo(() => {
    const columns: GridColDef[] = [
      // {
      //   field: 'index',
      //   headerName: 'Pos',
      //   maxWidth: 80,
      //   sortable: true,
      //   resizable: false,
      //   align: 'center',
      //   headerAlign: 'center',
      //   description: t('playlistPosition', 'Playlist position'),
      // },
      {
        field: 'votes',
        headerName: 'Votes',
        maxWidth: 100,
        sortable: true,
        resizable: false,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, request: TrackRequest) => {
          const priorities = request.priorities ?? [];
          const messages = request.messages ?? [];
          const hasLow = priorities.some((p) => p.value === TrackRequesterPriorityValue.Low);
          const hasHigh = priorities.some((p) => p.value === TrackRequesterPriorityValue.High);
          const hasMessages = messages.length > 0;
          const totalVotes = request.requesters?.length ?? 0;
          const normalVotesCount = Math.max(totalVotes - priorities.length, 0);
          const hasNoVotes = totalVotes === 0 && priorities.length === 0 && messages.length === 0;

          if (hasNoVotes) return 0;
          if (hasLow) return 1;
          if (hasMessages) return 2;
          if (normalVotesCount > 0) return 3;
          if (hasHigh) return 4;
          return 0;
        },
        renderCell: (params: GridRenderCellParams<TrackRequest>) => {
          const request = params.row;
          const messages = request.messages ?? [];
          const priorities = request.priorities ?? [];

          const highPriorities = priorities.filter(
            (p) => p.value === TrackRequesterPriorityValue.High
          );
          const lowPriorities = priorities.filter(
            (p) => p.value === TrackRequesterPriorityValue.Low
          );

          const totalVotes = request.requesters?.length ?? 0;
          const normalVotesCount = Math.max(totalVotes - priorities.length, 0);
          const hasNormalVotes = normalVotesCount > 0;
          const hasPriorityIcon = highPriorities.length > 0 || lowPriorities.length > 0;

          const badgeSx = {
            '& .MuiBadge-badge': {
              fontSize: 9,
              height: 14,
              minWidth: 14,
              padding: 0,
            },
          };

          // No votes anymore
          if (totalVotes === 0 && !hasPriorityIcon && !messages.length) {
            return (
              <Tooltip
                title={t(
                  'noVotes',
                  'Nobody has voted for this request, or all votes have been withdrawn.'
                )}
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                  }}
                >
                  <DoNotDisturbIcon
                    fontSize="small"
                    color="error"
                    // sx={{ color: theme.palette.action.disabled }}
                  />
                </Box>
              </Tooltip>
            );
          }

          const hasConflict = highPriorities.length > 0 && lowPriorities.length > 0;

          const priorityIcon = hasConflict ? (
            <ThumbsUpDownIcon fontSize="small" color="disabled" />
          ) : lowPriorities.length > 0 ? (
            <ThumbDownIcon fontSize="small" color="error" />
          ) : highPriorities.length > 0 ? (
            <StarIcon fontSize="small" color="warning" />
          ) : null;

          const priorityBadgeCount = hasConflict
            ? highPriorities.length + lowPriorities.length
            : lowPriorities.length > 0
            ? lowPriorities.length
            : highPriorities.length;

          return (
            <Tooltip
              title={
                <Box>
                  {hasPriorityIcon && (
                    <Box>
                      <Typography variant="caption" fontWeight="bold">
                        {t('priorities', 'Priorities')}
                      </Typography>
                      {highPriorities.length > 0 && (
                        <Typography variant="body2">
                          {t('highPriorityCount', '{{count}}x Important', {
                            count: highPriorities.length,
                          })}
                        </Typography>
                      )}
                      {lowPriorities.length > 0 && (
                        <Typography variant="body2">
                          {t('lowPriorityCount', '{{count}}x Dislike', {
                            count: lowPriorities.length,
                          })}
                        </Typography>
                      )}
                    </Box>
                  )}
                  {hasNormalVotes && (
                    <Box sx={{ mt: hasPriorityIcon ? 1 : 0 }}>
                      <Typography variant="caption" fontWeight="bold">
                        {t('votes', 'Votes')}
                      </Typography>
                      <Typography variant="body2">
                        {t('normalVoteCount', '{{count}}x Voted', { count: normalVotesCount })}
                      </Typography>
                    </Box>
                  )}
                  {messages.length > 0 && (
                    <Box sx={{ mt: hasPriorityIcon || hasNormalVotes ? 1 : 0 }}>
                      <Typography variant="caption" fontWeight="bold">
                        {t('messages', 'Messages')}
                      </Typography>
                      <Stack
                        direction="column"
                        spacing={0.5}
                        sx={{ flexWrap: 'wrap', alignItems: 'flex-start' }}
                      >
                        {messages.map((m, i) => (
                          <Chip
                            key={m.id ?? i}
                            label={m.value}
                            size="small"
                            sx={{
                              height: 'auto',
                              '& .MuiChip-label': {
                                display: 'block',
                                whiteSpace: 'normal',
                              },
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Box>
              }
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.5,
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                }}
              >
                {priorityIcon && (
                  <Badge
                    badgeContent={priorityBadgeCount > 1 ? priorityBadgeCount : 0}
                    color="secondary"
                    overlap="circular"
                    sx={badgeSx}
                  >
                    {priorityIcon}
                  </Badge>
                )}
                {hasNormalVotes && (
                  <Badge
                    badgeContent={normalVotesCount > 1 ? normalVotesCount : 0}
                    color="secondary"
                    overlap="circular"
                    sx={badgeSx}
                  >
                    <ThumbUpIcon fontSize="small" color="success" />
                  </Badge>
                )}
                {messages.length > 0 && (
                  <Badge
                    badgeContent={messages.length > 1 ? messages.length : 0}
                    color="secondary"
                    overlap="circular"
                    sx={badgeSx}
                  >
                    <ChatBubbleIcon fontSize="small" />
                  </Badge>
                )}
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: 'count',
        headerName: t('requestCount', 'Count'),
        maxWidth: 80,
        sortable: true,
        resizable: false,
        description: t('requestCountDescription', 'Request count'),
        // minWidth: 200,
        valueGetter: (value, request: TrackRequest<Track> | undefined) => {
          return request?.requesters?.length || 0;
        },
      },
      {
        field: 'vipMessages',
        headerName: t('vipMessages', 'VIP Messages'),
        sortable: true,
        flex: 1,
        valueGetter: (value, request: TrackRequest) =>
          request.messages?.map((m) => m.value).join(', ') ?? '',
        renderCell: (params: GridRenderCellParams<TrackRequest>) => {
          const messages = params.row.messages;

          if (!messages?.length) {
            return null;
          }

          return (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'nowrap',
                gap: 0.5,
                overflow: 'hidden',
                alignItems: 'center',
                height: '100%',
              }}
            >
              {messages.map((m, i) => (
                <Chip key={m.id ?? i} label={m.value} size="small" />
              ))}
            </Box>
          );
        },
      },
      {
        field: 'cover',
        headerName: '',
        resizable: false,
        width: 60,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<TrackRequest>) => {
          const request = params.row;
          const isTrackLike =
            request.type === TrackRequestType.Track || request.type === TrackRequestType.Library;

          return (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  overflow: 'hidden',
                  backgroundColor: theme.palette.secondary.main,
                }}
              >
                <TrackCover
                  title={isTrackLike ? (request.value as Track).title : (request.value as string)}
                  cover={isTrackLike ? (params.row.value as Track).artworkUrl : undefined}
                  fallback={'TEXT'}
                />
              </Box>
            </Box>
          );
        },
      },
      {
        field: 'title',
        headerName: t('title', 'Title'),
        flex: 1,
        hideable: false,
        valueGetter: (value, request: TrackRequest) => {
          const isTrackLike =
            request.type === TrackRequestType.Track || request.type === TrackRequestType.Library;
          return (
            request.localTrack?.title ||
            (isTrackLike ? (request.value as Track).title : (request.value as string))
          );
        },
        renderCell: (params: GridRenderCellParams<TrackRequest>) => {
          const request = params.row;
          const isTrackLike =
            request.type === TrackRequestType.Track || request.type === TrackRequestType.Library;
          const originalRequestValue = isTrackLike
            ? (request.value as Track).title
            : (request.value as string);

          if (request.localTrack?.title) {
            return (
              <DataGridColumnWithSubtitle
                title={originalRequestValue}
                subtitle={request.localTrack?.title}
              />
            );
          }

          return <>{originalRequestValue}</>;
        },
      },
      {
        field: 'artist',
        headerName: t('artist', 'Artist'),
        flex: 1,
        valueGetter: (value, request: TrackRequest) => {
          const isTrackLike =
            request.type === TrackRequestType.Track || request.type === TrackRequestType.Library;
          return (
            request.localTrack?.artist ||
            (isTrackLike ? (request.value as Track).artist : (request.value as string))
          );
        },
        renderCell: (params: GridRenderCellParams<TrackRequest>) => {
          const request = params.row;
          const isTrackLike =
            request.type === TrackRequestType.Track || request.type === TrackRequestType.Library;
          const originalRequestValue = isTrackLike
            ? (request.value as Track).artist
            : (request.value as string);

          if (request.localTrack?.artist) {
            return (
              <DataGridColumnWithSubtitle
                title={originalRequestValue}
                subtitle={request.localTrack?.artist}
              />
            );
          }

          return originalRequestValue;
        },
      },
      {
        field: 'collection',
        headerName: t('collection', 'Collection'),
        flex: 1,
        valueGetter: (value, request: TrackRequest) => {
          if (request.localTrack?.album) {
            return request.localTrack.album;
          }

          const isTrackLike =
            request.type === TrackRequestType.Track || request.type === TrackRequestType.Library;
          return isTrackLike ? (request.value as Track).collection : '';
        },
      },
      {
        field: 'bpm',
        headerName: t('bpm', 'BPM'),
        minWidth: 80,
        valueGetter: (value, request: TrackRequest) => request.localTrack?.bpm,
      },
      {
        field: 'key',
        headerName: t('key', 'Key'),
        minWidth: 80,
        valueGetter: (value, request: TrackRequest) => request.localTrack?.key,
      },
      {
        field: 'genre',
        headerName: t('genre', 'Genre'),
        minWidth: 80,
        valueGetter: (value, request: TrackRequest) => request.localTrack?.genre,
      },
      {
        field: 'duration',
        headerName: t('trackDuration', 'Duration'),
        minWidth: 80,
        valueGetter: (value, request: TrackRequest) =>
          (request.value as Track)?.duration
            ? formatDuration((request.value as Track)?.duration || 0)
            : null,
      },
      {
        field: 'path',
        headerName: t('trackPath', 'Path'),
        minWidth: 80,
        valueGetter: (value, request: TrackRequest) => request.localTrack?.path,
      },
    ];

    if (requestColumns?.includes('date')) {
      columns.push({
        field: 'date',
        headerName: t('requestDate', 'Request Date'),
        minWidth: 200,
        valueFormatter: (value) => dayjs(value).format('L LT'),
      });
    }

    if (requestColumns?.includes('deleteDate')) {
      columns.push({
        field: 'deleteDate',
        headerName: t('deleteDate', 'Delete Date'),
        minWidth: 200,
        valueFormatter: (value) => (value ? dayjs(value).format('L LT') : ''),
      });
    }

    if (requestColumns?.includes('state')) {
      columns.push({
        field: 'state',
        headerName: t('requestState', 'State'),
        maxWidth: 100,
        hideable: false,
        resizable: false,
        valueGetter: (value, request: TrackRequest<Track> | undefined) => {
          if (request && request.localId == null) {
            if (request.foundLocalTracks != null) {
              return request.foundLocalTracks.length > 1 ? 2 : 1;
            } else {
              return 3;
            }
          }

          return 0;
        },
        renderCell: (params: GridRenderCellParams<TrackRequest>) => {
          const count = params.row.foundLocalTracks?.length;

          const requestState: TrackRequestState =
            params.row.localId && !params.row.autoMapped
              ? TrackRequestState.Mapped
              : params.row.localId && params.row.autoMapped
              ? TrackRequestState.AutoMapped
              : count && count == 1
              ? TrackRequestState.FoundOne
              : count && count > 1
              ? TrackRequestState.FoundMultiple
              : TrackRequestState.NotFound;

          return (
            <ClientComponent
              fallback={<HighlightOffIcon sx={{ m: theme.spacing(1) }} color="secondary" />}
            >
              <RequestMapButton
                request={params.row}
                foundCount={count}
                state={requestState}
                onClick={() => onMapButtonClick(params.row, MapOption.MAP)}
              />
            </ClientComponent>
          );

          // return (
          // if (isElectron() && params.row.localId == null) {
          //   if (params.row.foundLocalTracks?.length) {
          //     return (
          //       <Tooltip title={`${count} song(s) found in your library`}>
          //         <IconButton
          //           color="warning"
          //           onClick={() => onMapButtonClick(params.row, MapOption.MAP)}
          //         >
          //           <HelpOutlineIcon />
          //         </IconButton>
          //       </Tooltip>
          //     );
          //   }

          //   return (
          //     <Tooltip title="Song not found in your library">
          //       <IconButton
          //         color="error"
          //         onClick={() => onMapButtonClick(params.row, MapOption.MAP)}
          //       >
          //         <ErrorOutlineIcon />
          //       </IconButton>
          //     </Tooltip>
          //   );
          // }
          // );

          // return (
          // <Box sx={{ p: theme.spacing(1) }}>
          //   <Tooltip title="In playlist">
          //     <CheckCircleIcon color="success" />
          //   </Tooltip>
          // </Box>
          // );
        },
      });
    }

    if (actions) {
      columns.push({
        field: 'actions',
        type: 'actions',
        headerName: '',
        sortable: false,
        minWidth: 150,
        hideable: false,
        resizable: false,
        renderCell: actions,
      });
    }

    return columns;
  }, []);

  const EmptyState = () => (
    <Box
      sx={{
        justifyContent: 'center',
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        p: 10,
      }}
    >
      <Box color="textDisabled">
        <SvgIcon color="disabled" sx={{ fontSize: 80 }}>
          <MusicOffIcon />
        </SvgIcon>
      </Box>
      <Typography variant="h2" color="textDisabled">
        {t('itsEmptyHere', "It's empty here...")}
      </Typography>
    </Box>
  );

  // if (!requests.length) {
  //   return (
  //     <Box
  //       sx={{
  //         justifyContent: 'center',
  //         alignItems: 'center',
  //         display: 'flex',
  //         flexDirection: 'column',
  //         p: 10,
  //       }}
  //     >
  //       <Box color="textDisabled">
  //         <SvgIcon color="disabled" sx={{ fontSize: 80 }}>
  //           <MusicOffIcon />
  //         </SvgIcon>
  //       </Box>
  //       <Typography variant="h2" color="textDisabled">
  //         {t('itsEmptyHere', "It's empty here...")}
  //       </Typography>
  //     </Box>
  //   );
  // }

  // const background =
  //   requesterPriority?.value === TrackRequesterPriorityValue.High
  //     ? `linear-gradient(90deg, ${alpha(theme.palette.warning.main, 0.12)} 0%, transparent 100%)`
  //     : requesterPriority?.value === TrackRequesterPriorityValue.Low
  //     ? `linear-gradient(90deg, ${alpha(theme.palette.error.main, 0.12)} 0%, transparent 100%)`
  //     : isRequestedByCurrentUser
  //     ? `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.12)} 0%, transparent 100%)`
  //     : 'transparent';

  return (
    <Box sx={{ width: '100%', minHeight: 200 }}>
      <DataGrid
        autoHeight
        rows={requests}
        getRowClassName={getRowClassName}
        sx={{
          border: 0,
          '& .MuiDataGrid-overlayWrapper': {
            minHeight: requests.length ? 'auto' : 200,
          },
          '& .row-priority-high': {
            background: `linear-gradient(90deg, ${alpha(
              theme.palette.warning.main,
              0.12
            )} 0%, transparent 100%)`,
          },
          '& .row-priority-low': {
            background: `linear-gradient(90deg, ${alpha(
              theme.palette.error.main,
              0.12
            )} 0%, transparent 100%)`,
          },
          '& .row-no-votes': {
            background: `linear-gradient(90deg, ${alpha(
              theme.palette.error.main,
              0.12
            )} 0%, transparent 100%)`,
          },
          '& .row-priority-high:hover': {
            backgroundColor: alpha(theme.palette.warning.main, 0.2),
          },
          '& .row-priority-low:hover': {
            backgroundColor: alpha(theme.palette.error.main, 0.2),
          },
          '& .row-no-votes:hover': {
            backgroundColor: alpha(theme.palette.error.main, 0.2),
          },
        }}
        getRowId={(row) => row.id || JSON.stringify(row)}
        columns={requestListColumns}
        hideFooterSelectedRowCount
        pageSizeOptions={[{ value: -1, label: t('all', 'All') }]}
        disableColumnMenu
        disableRowSelectionOnClick
        checkboxSelection
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={(model) => {
          setSelectionModel(model);
          const selected =
            model.type === 'include'
              ? requests.filter((r) => r.id && model.ids.has(r.id))
              : requests.filter((r) => r.id && !model.ids.has(r.id));
          onSelectRequest?.(selected);
        }}
        slots={{ noRowsOverlay: EmptyState, ...slots }}
        {...rest}
      />
    </Box>
    // <Box sx={{ width: '100%' }}>
    //   <DataGrid
    //     rows={requests}
    //     sx={{
    //       border: 0,
    //     }}
    //     getRowId={(row) => row.id || JSON.stringify(row)}
    //     columns={requestListColumns}
    //     hideFooterSelectedRowCount
    //     // pageSizeOptions={[50, 100, { value: -1, label: 'All' }]}
    //     pageSizeOptions={[{ value: -1, label: t('all', 'All') }]}
    //     disableColumnMenu
    //     disableRowSelectionOnClick
    //     checkboxSelection
    //     rowSelectionModel={selectionModel}
    //     onRowSelectionModelChange={(model) => {
    //       setSelectionModel(model);

    //       const selected =
    //         model.type === 'include'
    //           ? requests.filter((r) => r.id && model.ids.has(r.id))
    //           : requests.filter((r) => r.id && !model.ids.has(r.id));

    //       onSelectRequest?.(selected);
    //     }}
    //     {...rest}
    //   />
    // </Box>
  );
};
